/**
 * sanityResolutionMigrate — collapse legacy three-state sanity-finding
 * resolutions (`approved` / `changed` / `discarded`, plus the never-
 * shipped-but-spec'd `approved-apply`) onto the two-state model
 * (`apply` / `ignore`) introduced by the Apply-on-Rerun pack.
 *
 * Mapping rationale (see Report_Insights_Tray_Master_Prompts.md §D2 and
 * the Apply_On_Rerun_Queue_Prompt.md migration-safety note):
 *
 *   - `'approved'`       → `'ignore'`.  "Approved" in the old model meant
 *     "acknowledged, don't flag again" — equivalent to the new "ignore".
 *     Mapping it to "apply" would silently rewrite report bodies for
 *     findings the user never intended to apply, so we default safe and
 *     let users explicitly choose "apply" going forward.
 *   - `'discarded'`      → `'ignore'`.  Same suppress-only semantics.
 *   - `'changed'`        → `'apply'` with the userNote feeding
 *     `applyDirective.suggestionAtResolution` (the user *did* intend an
 *     edit; carry it forward).
 *   - `'approved-apply'` → `'apply'`, preserving any existing directive.
 *   - already-new values (`'apply'` / `'ignore'`) pass through unchanged.
 *   - no resolution                     → no resolution.
 *
 * Idempotent: running it again on an already-migrated finding is a
 * no-op. The store flips `report.sanityResolutionsMigrated` after the
 * first pass and persists, so the migration runs once per record.
 */
import type { ApplyDirective, SanityFinding } from '../types/siteRag';

/** The set of legacy values plus the new ones, so callers reading raw
 *  Firestore data (typed loosely) can be narrowed without an `any`. */
export type LegacySanityResolution =
  | 'approved'
  | 'changed'
  | 'discarded'
  | 'approved-apply'
  | 'apply'
  | 'ignore'
  | undefined
  | null;

export interface MigratedResolution {
  resolution: SanityFinding['resolution'];
  userNote?: string;
  applyDirective?: ApplyDirective;
}

/**
 * Map one legacy/new resolution onto the two-state model.
 *
 * @param legacy            the raw `resolution` value off the persisted finding
 * @param finding           the finding (for `section` + `suggestion` fallbacks)
 * @param existingDirective an `applyDirective` already on the finding, if any
 * @param existingNote      the `userNote` already on the finding, if any
 */
export function migrateSanityResolution(
  legacy: LegacySanityResolution,
  finding: Pick<SanityFinding, 'section' | 'suggestion'>,
  existingDirective?: ApplyDirective,
  existingNote?: string,
): MigratedResolution {
  switch (legacy) {
    case undefined:
    case null:
      return { resolution: undefined };

    case 'ignore':
    case 'approved':
    case 'discarded':
      // Suppress-only. Drop any stale directive — an ignored finding
      // must not carry an applyDirective into the next rerun.
      return { resolution: 'ignore', userNote: existingNote };

    case 'apply':
    case 'approved-apply': {
      // Already-apply (or the spec'd approved-apply) — keep the existing
      // directive when present, else synthesise one from the suggestion.
      const directive: ApplyDirective = existingDirective ?? {
        suggestionAtResolution: existingNote && existingNote.trim().length > 0
          ? existingNote
          : finding.suggestion,
        section: finding.section,
      };
      return { resolution: 'apply', userNote: existingNote, applyDirective: directive };
    }

    case 'changed': {
      // The user intended an edit — the userNote is that edit. Promote
      // it to an apply directive; fall back to the AI suggestion if the
      // note was empty.
      const note = existingNote && existingNote.trim().length > 0 ? existingNote : undefined;
      const directive: ApplyDirective = existingDirective ?? {
        suggestionAtResolution: note ?? finding.suggestion,
        section: finding.section,
      };
      return { resolution: 'apply', userNote: note, applyDirective: directive };
    }

    default: {
      // Exhaustiveness guard — if a new legacy value is ever added the
      // compiler will flag this. At runtime, default safe to ignore.
      const _exhaustive: never = legacy;
      void _exhaustive;
      return { resolution: 'ignore' };
    }
  }
}

/**
 * Migrate every finding on a report's `sanityFindings` array. Returns a
 * new array; the input is not mutated. `changed` is true when any
 * finding's resolution/userNote/applyDirective actually moved (so the
 * caller can skip the Firestore write when nothing changed).
 */
export function migrateSanityFindings(
  findings: SanityFinding[] | undefined,
): { findings: SanityFinding[]; changed: boolean } {
  if (!findings || findings.length === 0) return { findings: findings ?? [], changed: false };

  let changed = false;
  const next = findings.map((f) => {
    const legacy = f.resolution as LegacySanityResolution;
    const m = migrateSanityResolution(legacy, f, f.applyDirective, f.userNote);
    const moved =
      m.resolution !== f.resolution ||
      m.userNote !== f.userNote ||
      JSON.stringify(m.applyDirective ?? null) !== JSON.stringify(f.applyDirective ?? null);
    if (!moved) return f;
    changed = true;
    // resolvedAt is preserved as-is; only the resolution shape changes.
    const updated: SanityFinding = { ...f, resolution: m.resolution };
    if (m.userNote !== undefined) updated.userNote = m.userNote;
    else delete updated.userNote;
    if (m.applyDirective !== undefined) updated.applyDirective = m.applyDirective;
    else delete updated.applyDirective;
    return updated;
  });
  // No element moved → hand back the original reference so callers can
  // cheaply skip a persist (and so `===` holds for the idempotency check).
  return changed ? { findings: next, changed: true } : { findings, changed: false };
}
